/**
 * Contrat wire des actions canvas de rédaction (agent ↔ SuperDoc).
 *
 * SOURCE UNIQUE des 16 kinds d'actions `canvas_actions_request` et des
 * whitelists de styles Hector. Avant ce module (2026-07-03), le contrat
 * vivait en 3 copies désynchronisées : union 5-kinds backend
 * (`apps/backend/types/hector-agent-edition-v3/websocket.ts`), union
 * 16-kinds frontend (`canvasBridge.ts`), et 3 whitelists de styles « sync
 * par convention ». Validé par Zod aux deux frontières : à l'ÉMISSION
 * (`ResponseStreamer.sendCanvasActionsRequest`, backend) et à la RÉCEPTION
 * (handler `canvas_actions_request` de `WebSocketPool`, frontend).
 */
import { z } from 'zod';
import { CanvasTextListPrefixSchema } from './textListPrefix.js';
// Le vocabulaire de conduite (`reinspect | retry | rollback | stop`) est celui
// du catalogue d'actions : les receipts d'exécution s'en servent déjà, les refus
// émis AVANT exécution s'en servent ici. Une seule notion, pas deux.
// (Ce module est re-exporté en bas de fichier ; l'import n'introduit pas de
// cycle, `actionCatalog.ts` ne dépend que de `zod` et de `../webUrl.js`.)
import { CanvasDraftKindSchema } from './canvasDraft.js';
import { CanvasFormatReadingScopeSchema } from './canvasFormat.js';
import {
  CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH,
  CANVAS_INSPECT_BLOCK_LIMIT_MAX,
  CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX,
  CanvasActionNameSchema,
  CanvasCapabilityQualificationSchema,
  RecoveryInstructionSchema,
  type RecoveryInstruction,
} from './actionCatalog.js';

// ── Styles Hector ───────────────────────────────────────────────────────────

/**
 * `Hector-Hn` → heading level. Gérés par node-type heading côté éditeur
 * (outlineLevel Word + Navigation Pane), PAS par pStyle — c'est pourquoi ils
 * ne figurent pas dans {@link HECTOR_STYLE_IDS_PRESET}.
 */
export const HECTOR_HEADING_STYLE_LEVELS: Readonly<Record<string, number>> = {
  'Hector-H1': 1,
  'Hector-H2': 2,
  'Hector-H3': 3,
  'Hector-H4': 4,
  'Hector-H5': 5,
  'Hector-H6': 6,
};

/**
 * styleIds préinstallés dans le template DOCX du canvas
 * (`apps/legalworkspace/scripts/generate-blank-template.ts`). Un `apply_style`
 * hors de ce set (et hors headings) est une ERREUR explicite — jamais de
 * fallback silencieux (l'ancien `setNode('paragraph')` renvoyait success:true
 * sans styling, le LLM croyait avoir stylé le paragraphe).
 *
 * NB 2026-07-03 : `Hector-Italique` était exposé au LLM (outils canvas backend +
 * ToolDefinitions) sans exister dans le template ni ici → retiré partout.
 */
export const HECTOR_STYLE_IDS_PRESET: ReadonlySet<string> = new Set([
  'Hector-Normal',
  'Hector-Normal-Compact',
  'Hector-Quote',
  'Hector-Liste',
  'Hector-PLAISE',
  'Hector-PLAISE-Body',
  'Hector-BORDEREAU',
  'Hector-Pieces-Jointes',
  'Hector-PAR-CES-MOTIFS',
  'Hector-TRES-IMPORTANT',
  'Hector-Marqueur-BU',
  'Hector-Marqueur-Bold',
  'Hector-Party-Label',
  'Hector-Piece-Before',
  'Hector-Piece-After',
  'Hector-Separator',
  'Hector-Custom1',
  'Hector-Custom2',
  'Hector-Custom3',
  'Hector-Custom4',
  'Hector-Custom5',
  'Hector-Custom6',
  'Hector-Custom7',
  'Hector-Custom8',
  'Hector-Custom9',
  'Hector-Custom10',
]);

/**
 * Styles exposés au LLM dans les tool definitions (sous-ensemble volontairement
 * restreint du preset : le LLM n'a pas besoin des Custom1-10). Source unique
 * consommée par les outils canvas backend (validation d'args) et les prompts.
 */
export const CANVAS_ALLOWED_STYLES: ReadonlySet<string> = new Set([
  'Hector-Normal',
  'Hector-H1',
  'Hector-H2',
  'Hector-H3',
  'Hector-H4',
  'Hector-H5',
  'Hector-H6',
  'Hector-PLAISE',
  'Hector-BORDEREAU',
  'Hector-PAR-CES-MOTIFS',
  'Hector-TRES-IMPORTANT',
  'Hector-Marqueur-BU',
  'Hector-Marqueur-Bold',
  'Hector-Pieces-Jointes',
  'Hector-Piece-Before',
]);

// ── Actions (16 kinds — shapes verrouillées, copie exacte du bridge) ────────

const styleField = z.string().min(1);

/**
 * Union discriminée des 16 actions de mutation canvas. Les shapes sont la
 * copie EXACTE du contrat implicite historique (y compris l'incohérence de
 * nommage `index` vs `paragraph_index` — contractualisée telle quelle, la
 * réforme d'adressage est le sujet de la taxonomie v2 / PR ③).
 */
export const CanvasActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('insert_paragraph'), after_index: z.number().int(), text: z.string(), style: styleField }),
  z.object({ kind: z.literal('delete_paragraph'), index: z.number().int().nonnegative() }),
  z.object({ kind: z.literal('replace_paragraph'), index: z.number().int().nonnegative(), text: z.string(), style: styleField.optional() }),
  z.object({
    kind: z.literal('search_replace'),
    paragraph_index: z.number().int().nonnegative(),
    search_text: z.string().min(1),
    replace_text: z.string(),
  }),
  z.object({ kind: z.literal('apply_style'), index: z.number().int().nonnegative(), style: styleField }),
  z.object({
    kind: z.literal('apply_text_format'),
    paragraph_index: z.number().int().nonnegative(),
    search_text: z.string().min(1),
    format: z.object({
      bold: z.boolean().optional(),
      italic: z.boolean().optional(),
      underline: z.boolean().optional(),
    }),
  }),
  z.object({
    kind: z.literal('apply_font'),
    paragraph_index: z.number().int().nonnegative(),
    font_name: z.string().optional(),
    font_size: z.number().optional(),
  }),
  z.object({ kind: z.literal('clear_formatting'), paragraph_index: z.number().int().nonnegative() }),
  z.object({
    kind: z.literal('set_alignment'),
    paragraph_index: z.number().int().nonnegative(),
    alignment: z.enum(['left', 'center', 'right', 'justify']),
  }),
  z.object({
    kind: z.literal('highlight_range'),
    paragraph_index: z.number().int().nonnegative(),
    search_text: z.string().min(1),
    color: z.enum(['yellow', 'pink', 'green']).optional(),
  }),
  z.object({
    kind: z.literal('insert_comment'),
    paragraph_index: z.number().int().nonnegative(),
    search_text: z.string().min(1),
    comment_text: z.string().min(1),
  }),
  z.object({ kind: z.literal('navigate_to_paragraph'), paragraph_index: z.number().int().nonnegative() }),
  z.object({ kind: z.literal('resolve_comment'), comment_id: z.string().min(1) }),
  z.object({ kind: z.literal('reopen_comment'), comment_id: z.string().min(1) }),
  z.object({ kind: z.literal('delete_comment'), comment_id: z.string().min(1) }),
  z.object({ kind: z.literal('set_active_comment'), comment_id: z.string().min(1).nullable() }),
]);
export type CanvasAction = z.infer<typeof CanvasActionSchema>;

/** Payload complet d'un message WS `canvas_actions_request`. */
export const CanvasActionsRequestPayloadSchema = z.object({
  requestId: z.string().min(1),
  actions: z.array(CanvasActionSchema),
});
export type CanvasActionsRequestPayload = z.infer<typeof CanvasActionsRequestPayloadSchema>;

// ── SuperDoc-native wire contract (tool-based request/receipt) ─────────────

export const CanvasSuperDocToolNameSchema = z.enum([
  'canvas_inspect',
  'canvas_perform_action',
]);

const CanvasInspectionReadRangeBaseSchema = z
  .object({
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
  })
  .strict();

const CanvasInspectionReadRangeSchema = CanvasInspectionReadRangeBaseSchema
  .superRefine((range, context) => {
    if (range.end < range.start) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['end'], message: 'range.end must be greater than or equal to range.start.' });
    }
  });

const CanvasInspectionPublicRangeSchema = CanvasInspectionReadRangeBaseSchema.extend({
  nodeId: z.string().min(1),
  nodeType: z.string().min(1),
}).superRefine((range, context) => {
  if (range.end < range.start) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['end'], message: 'range.end must be greater than or equal to range.start.' });
  }
});

const CanvasInspectionReadContinuationSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('blockWindow'),
    blockOffset: z.number().int().nonnegative(),
    blockLimit: z.number().int().positive().max(CANVAS_INSPECT_BLOCK_LIMIT_MAX),
  }).strict(),
  z.object({
    kind: z.literal('textWindow'),
    nodeId: z.string().min(1),
    textWindow: z.object({
      offset: z.number().int().nonnegative(),
      limit: z.number().int().positive().max(CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH),
    }).strict(),
  }).strict(),
]);

/**
 * Metadata needed to continue a bounded public block without adding new keys
 * to the long-lived strict `data.blocks` receipt during a front-first rollout.
 */
export const CanvasInspectionTextContinuationSchema = z
  .object({
    nodeId: z.string().min(1),
    textLength: z.number().int().positive(),
    textRange: CanvasInspectionReadRangeSchema,
    textWindow: z.object({
      offset: z.number().int().nonnegative(),
      limit: z.number().int().positive().max(CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH),
    }).strict(),
  })
  .strict()
  .superRefine((continuation, context) => {
    if (continuation.textRange.end > continuation.textLength) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['textRange', 'end'], message: 'textRange must stay inside textLength.' });
    }
    if (continuation.textRange.end >= continuation.textLength) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['textRange', 'end'], message: 'A text continuation requires a non-empty omitted suffix.' });
    }
    if (continuation.textWindow.offset !== continuation.textRange.end) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['textWindow', 'offset'], message: 'textWindow.offset must resume exactly at textRange.end.' });
    }
    if (continuation.textWindow.limit > continuation.textLength - continuation.textWindow.offset) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['textWindow', 'limit'], message: 'textWindow.limit must not exceed the omitted suffix.' });
    }
  });
export type CanvasInspectionTextContinuation = z.infer<typeof CanvasInspectionTextContinuationSchema>;

export const CanvasTextSearchMissSchema = z.object({
  match: z.literal('all'),
  blocksHoldingEachTerm: z.array(z.object({ term: z.string(), blocks: z.number().int().nonnegative() }).strict()).min(2),
}).strict();
export type CanvasTextSearchMiss = z.infer<typeof CanvasTextSearchMissSchema>;

/**
 * Live content returned by `canvas_inspect` without changing the strict
 * mutation receipt in `data`. Public ranges come from the same native
 * projection as `data.blocks`: since S6 (2026-10-01) the backend broker arms
 * the ranges of a fresh `final` document read of an editable document as
 * targeted scope, then shows them to the model as `⟦#nodeId⟧` markers in
 * `content` and removes `publicRanges` from the model view.
 */
export const CanvasInspectionReadSchema = z
  .object({
    scope: z.enum(['document', 'textWindow']),
    content: z.string().max(CANVAS_DOCUMENT_READ_CONTENT_MAX_LENGTH),
    completeness: z.object({
      status: z.enum(['complete', 'incomplete']),
      totalCharacters: z.number().int().nonnegative(),
      returnedRange: CanvasInspectionReadRangeSchema,
      totalBlocks: z.number().int().nonnegative(),
      totalBlocksExact: z.boolean(),
    }).strict(),
    publicRanges: z.array(CanvasInspectionPublicRangeSchema).max(CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX),
    /** Literal prefixes for public nodeIds published by this read; preserve them when rewriting their content. */
    textListPrefixes: z.array(CanvasTextListPrefixSchema.extend({ nodeId: z.string().min(1) }))
      .max(CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX).optional(),
    publicRangesTotal: z.number().int().nonnegative(),
    publicRangesComplete: z.boolean(),
    continuation: CanvasInspectionReadContinuationSchema.optional(),
  })
  .strict()
  .superRefine((read, context) => {
    const { completeness } = read;
    const { returnedRange } = completeness;
    if (returnedRange.end > completeness.totalCharacters) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['completeness', 'returnedRange', 'end'], message: 'returnedRange must stay inside totalCharacters.' });
    }
    if (read.content.length !== returnedRange.end - returnedRange.start) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['content'], message: 'content length must equal the declared returnedRange; do not silently truncate an inspection read.' });
    }
    if (read.publicRangesTotal < read.publicRanges.length) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['publicRangesTotal'], message: 'publicRangesTotal cannot be smaller than the returned publicRanges list.' });
    }
    if (read.publicRangesComplete !== (read.publicRangesTotal === read.publicRanges.length)) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['publicRangesComplete'], message: 'publicRangesComplete must state whether every public range was returned.' });
    }
    read.publicRanges.forEach((range, index) => {
      if (range.start < returnedRange.start || range.end > returnedRange.end) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['publicRanges', index],
          message: 'A public range must stay inside the inspection content actually returned to the model.',
        });
      }
    });
    if (completeness.status === 'complete') {
      const coversWholeDocument = read.scope === 'document'
        && returnedRange.start === 0
        && returnedRange.end === completeness.totalCharacters;
      const reachesEndOfTextWindow = read.scope === 'textWindow'
        && returnedRange.end === completeness.totalCharacters;
      if (!coversWholeDocument && !reachesEndOfTextWindow) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['completeness', 'returnedRange'], message: 'a complete inspection read must cover its whole logical text.' });
      }
      if (read.continuation !== undefined) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['continuation'], message: 'a complete inspection read must not advertise a continuation.' });
      }
    } else if (read.continuation === undefined) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ['continuation'], message: 'an incomplete inspection read must advertise an explicit continuation.' });
    }
  });
export type CanvasInspectionRead = z.infer<typeof CanvasInspectionReadSchema>;

/**
 * Provider-level evidence required for the sole empty-body initialisation
 * exception. It deliberately stays outside the historical strict inspection
 * receipt in `data`: older backends may still read an inspection, while a
 * newer broker fails closed if this evidence is absent.
 *
 * The literals record the query breadth, rather than exposing an opaque
 * boolean. A future provider query with narrower defaults cannot silently
 * become proof that a legal document has no review artefacts.
 */
export const CanvasEmptyDocumentProofSchema = z.object({
  revision: z.string().min(1),
  /**
   * The OOXML body can be literally absent or contain Word's required one
   * empty paragraph. This explicit shape prevents a generic empty-looking
   * block from becoming whole-document write authority.
   */
  body: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('zero_blocks') }).strict(),
    z.object({
      kind: z.literal('single_empty_paragraph'),
      nodeId: z.string().min(1),
    }).strict(),
  ]),
  trackedChanges: z.object({
    scope: z.literal('all'),
    total: z.literal(0),
  }).strict(),
  comments: z.object({
    includeResolved: z.literal(true),
    includeThreadReplies: z.literal(true),
    total: z.literal(0),
  }).strict(),
}).strict();
export type CanvasEmptyDocumentProof = z.infer<typeof CanvasEmptyDocumentProofSchema>;

const canvasResultSuccessSchema = z.object({
  requestId: z.string().min(1),
  /** Echoed from the request so the server can reject an async callback from an older mount. */
  documentSessionId: z.string().min(1).optional(),
  /**
   * L'autre forme du même écho, pendant le retrait de `documentSessionId`
   * (#1335) : un client renouvelé identifie son montage par
   * `(conversationId, mountEpoch)` et n'émet plus `documentSessionId`.
   *
   * ⚠️ **Sa DÉCLARATION ici est load-bearing**, exactement comme celle de
   * `recovery` plus bas : ce schéma est ré-appliqué CÔTÉ BACKEND sur l'accusé
   * reçu, et Zod retire d'un `z.object` toute clé qu'il ne déclare pas. Sans
   * cette ligne, la conversation ré-énoncée disparaîtrait en silence entre le
   * navigateur et la fence de `SessionState.resolveCanvasOpResult` — qui, ne
   * voyant plus rien, jugerait le callback non corrélé et laisserait l'agent
   * attendre jusqu'au timeout.
   */
  conversationId: z.string().uuid().optional(),
  mountEpoch: z.number().int().nonnegative().optional(),
  /** Echoed only for mutations: the callback remains tied to the revision it was authorised against. */
  expectedRevision: z.string().min(1).optional(),
  ok: z.literal(true),
  data: z.unknown().optional(),
  /**
   * Diagnostic de capacités attaché seulement à un résultat `canvas_inspect`.
   *
   * Il reste hors de `data` pendant la transition : les anciens backends
   * revalident `data` contre un receipt strict et rejetteraient toute clé
   * nouvelle. Les objets de résultat externes, eux, sont déjà non stricts : un
   * backend antérieur ignore cette clé sans perdre l'inspection, tandis que le
   * pont `executableActions: []` le fait déjà échouer fermé pour une mutation.
   */
  capabilityQualification: CanvasCapabilityQualificationSchema.optional(),
  /**
   * Content-bearing Canvas inspection material is intentionally outside `data`.
   * Older backends revalidate the strict V2 receipt in `data`; adding it there
   * would turn a successful live read into an invalid receipt during rollout.
   */
  inspectionRead: CanvasInspectionReadSchema.optional(),
  /**
   * Narrow provider evidence for initialising an otherwise blank body. It is
   * independent from `inspectionRead`: the latter is model context, while this
   * proves that the two review discovery queries were exhaustive at a revision.
   */
  emptyDocumentProof: CanvasEmptyDocumentProofSchema.optional(),
  /**
   * Continuations for targeted long blocks. This remains outside `data` so an
   * older backend keeps accepting the historical strict block receipt.
   */
  inspectionTextContinuations: z.array(CanvasInspectionTextContinuationSchema)
    .max(CANVAS_INSPECT_PUBLIC_RANGE_LIMIT_MAX)
    .optional(),
  /**
   * Ce qu'une lecture avec forme (`formatReading`) arme pour les écritures de
   * texte. Hors de `data` comme `inspectionRead`, et pour la même raison de
   * déploiement ; ⚠️ sa déclaration ici est load-bearing : sans elle Zod
   * retire la clé à la frontière WebSocket, et le courtier n'arme rien.
   */
  formatReadingScope: CanvasFormatReadingScopeSchema.optional(),
  /**
   * Why a `textSearch` of several terms found no block: `match:"all"` wants them all in ONE block, and this counts
   * the blocks that hold each term alone. Published only on that empty result.
   *
   * WHY: `selectorResolution.matchCount: 0` alone reads as “the search is broken”. An agent that listed four keywords
   * of four different paragraphs concluded exactly that, twice, and fell back to whole-document reads (production
   * conversation, 2026-10-11). Outside `data`, whose strict schema an older backend still validates.
   */
  textSearchMiss: CanvasTextSearchMissSchema.optional(),
  error: z.never().optional(),
  code: z.never().optional(),
  capability: z.never().optional(),
});

const canvasResultFailureSchema = z.object({
  requestId: z.string().min(1),
  documentSessionId: z.string().min(1).optional(),
  /** Voir `canvasResultSuccessSchema.conversationId` — même écho, même raison d'être déclaré (#1335). */
  conversationId: z.string().uuid().optional(),
  mountEpoch: z.number().int().nonnegative().optional(),
  expectedRevision: z.string().min(1).optional(),
  ok: z.literal(false),
  error: z.string().min(1),
  code: z.string().min(1).optional(),
  capability: z.string().min(1).optional(),
  data: z.unknown().optional(),
  /**
   * La conduite à tenir, dite en structure — voir {@link RecoveryInstructionSchema}.
   *
   * ⚠️ **Sa DÉCLARATION ici est load-bearing, exactement comme celle de
   * `footnotes` sur le receipt d'inspection, et pour la raison INVERSE.**
   * `CanvasSuperDocResultPayloadSchema` est ré-appliqué CÔTÉ BACKEND sur le
   * receipt reçu (`websocket/schemas.ts`, puis
   * `SuperDocCanvasTools.sendAndAwaitCanvasPayload`), et Zod retire d'un
   * `z.object` toute clé qu'il ne déclare pas. Un `recovery` émis par le front
   * sans être déclaré ici ne ferait donc PAS échouer le parse : il
   * disparaîtrait en silence entre le navigateur et le modèle. Un test frontal
   * qui l'observe sur la trame émise serait vert, et l'agent ne le recevrait
   * jamais en production — c'est le mode d'échec que ce champ ferme.
   *
   * Optionnel au contrat : la plupart des refus n'ont pas de conduite unique à
   * prescrire, et un `recovery` absent veut dire « à toi de juger », jamais
   * « continue ». Seuls les refus dont l'issue est CERTAINE le posent.
   */
  recovery: RecoveryInstructionSchema.optional(),
});

const createCanvasToolResultSchema = <
  TTool extends z.infer<typeof CanvasSuperDocToolNameSchema>,
  TAction extends z.ZodTypeAny,
>(
  tool: TTool,
  action: TAction,
) =>
  z.discriminatedUnion('ok', [
    canvasResultSuccessSchema.extend({
      tool: z.literal(tool),
      action,
    }),
    canvasResultFailureSchema.extend({
      tool: z.literal(tool),
      action,
    }),
  ]);

export const CanvasSuperDocResultPayloadSchema = z.union([
  createCanvasToolResultSchema('canvas_inspect', z.literal('inspect')),
  createCanvasToolResultSchema(
    'canvas_perform_action',
    // Le receipt est validé à la frontière navigateur → backend. Il doit donc
    // accepter TOUT le catalogue, sans recopier sa liste : un nom oublié ici
    // laisserait l'écriture aboutir puis ferait disparaître son reçu, poussant
    // l'agent à rejouer une mutation peut-être déjà appliquée. Les deux littéraux
    // ajoutés sont des reliquats transport tolérés, absents du catalogue actif.
    z.union([
      CanvasActionNameSchema,
      z.literal('format_paragraph'),
      z.literal('list_tracked_changes'),
    ]),
  ),
]);
export type CanvasSuperDocResultPayload = z.infer<typeof CanvasSuperDocResultPayloadSchema>;

// ── Ouverture du canvas demandée par l'agent (`open_canvas`) ───────────────
//
// Contrat volontairement MINUSCULE, et c'est le sujet : l'outil supprimé
// `enable_redaction_mode` demandait l'ouverture en fire-and-forget et ne savait
// jamais si elle avait abouti. Ici la demande est corrélée par `requestId` et
// n'est réputée réussie que sur un accusé du navigateur, émis APRÈS que
// l'éditeur SuperDoc soit devenu écrivable — pas au moment où l'interrupteur
// bascule. Un canvas « ouvert » dont l'éditeur n'a pas fini de se construire
// ferait échouer le `canvas_inspect` que l'agent enchaîne immédiatement.

/**
 * Demande backend → front d'ouvrir le canvas.
 *
 * `depart` ne sert que si le fil n'a encore AUCUN document : `open_canvas` ne remplace rien, il
 * montre ce que le canvas tient déjà ; quand il ne tient rien, la page qu'il monte part de ce
 * papier à en-tête plutôt que du défaut du cabinet. Absent : le défaut (backend antérieur).
 */
export const CanvasOpenRequestPayloadSchema = z.object({
  requestId: z.string().min(1),
  depart: z.lazy(() => CanvasDocumentDepartSchema).optional(),
});
export type CanvasOpenRequestPayload = z.infer<typeof CanvasOpenRequestPayloadSchema>;

/**
 * Accusé front → back. `alreadyOpen` est la seule donnée transportée par le
 * succès : elle sert à l'agent pour ne PAS annoncer à l'avocat une ouverture
 * qui n'a rien changé à son écran. La session montée n'y figure pas
 * délibérément — elle voyage par `canvas_document_state`, seul canal du broker,
 * et la dupliquer ici créerait deux vérités concurrentes sur le même fait.
 */
export const CanvasOpenResultPayloadSchema = z.discriminatedUnion('ok', [
  z.object({
    requestId: z.string().min(1),
    ok: z.literal(true),
    alreadyOpen: z.boolean(),
    /** Current mount that produced the ACK; optional for rolling-deploy reads. */
    mountEpoch: z.number().int().nonnegative().optional(),
  }),
  z.object({
    requestId: z.string().min(1),
    ok: z.literal(false),
    error: z.string().min(1),
  }),
]);
export type CanvasOpenResultPayload = z.infer<typeof CanvasOpenResultPayloadSchema>;

// ── Ouverture d'un AUTRE document dans le canvas (`open_canvas_document`) ──
//
// Distinct d'`open_canvas`, et la distinction est le sujet : `open_canvas` est
// IDEMPOTENT et ne remplace rien — il montre ce que le canvas tient déjà. Celui-ci
// REMPLACE le document courant, donc il emprunte le chemin de remplacement exact
// (`replaceDocument`), avec sa capture avant destruction et son rollback visuel
// (D4, #1338/R) : si le document entrant ne monte pas, l'avocat retrouve le sien.
//
// Deux sources, une seule trame : un document vierge, ou un brouillon existant
// désigné par son identifiant. Les deux partagent le même bail, la même capture et
// le même retour arrière — les séparer en deux trames dupliquerait ces trois
// garanties, et une divergence de l'une d'elles ne se verrait pas.

/**
 * Sur quoi part un document neuf du canvas.
 *
 * - `defaut` : le papier de l'en-tête par défaut du cabinet, sinon le gabarit Hector.
 * - `aucun` : le gabarit Hector, sans en-tête ni pied, même si le cabinet a un papier. C'est la
 *   valeur `"aucun"` du paramètre `modele_de_page` des outils de l'agent.
 * - `nomme` : le papier d'un en-tête précis. L'identifiant est résolu et son appartenance au
 *   cabinet prouvée par celui qui l'émet (le backend pour l'agent, la liste des en-têtes du
 *   cabinet pour l'avocat) ; `name` ne sert qu'aux messages.
 *
 * C'est une RÉPONSE portée jusqu'au point de décision du canvas (l'effet de chargement de
 * `RedactionCanvas`), jamais un document : le papier ne voyage pas dans la trame.
 */
export const CanvasDocumentDepartSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('defaut') }),
  z.object({ kind: z.literal('aucun') }),
  z.object({ kind: z.literal('nomme'), letterheadId: z.string().uuid(), name: z.string().min(1) }),
]);
export type CanvasDocumentDepart = z.infer<typeof CanvasDocumentDepartSchema>;

/** La source du document à poser dans le canvas. */
export const CanvasOpenDocumentSourceSchema = z.discriminatedUnion('kind', [
  // `depart` absent vaut `defaut` : un backend antérieur ne l'émet pas.
  z.object({ kind: z.literal('blank'), depart: CanvasDocumentDepartSchema.optional() }),
  z.object({
    kind: z.literal('draft'),
    draftId: z.string().min(1),
    /**
     * Titre lu en base par le backend et transporté ici : le contenu d'un brouillon
     * se télécharge (`getDraftContent`) mais ne porte pas son propre titre, et le
     * navigateur en a besoin pour la présentation du document monté.
     */
    title: z.string().min(1),
    /**
     * La conversation à laquelle ce brouillon appartient, lue en base par le
     * backend en même temps que le titre.
     *
     * ⚠️ **Elle décide de la nature du geste, pas de son droit** (2026-09-22).
     * Un brouillon du fil courant est ADOPTÉ — l'avocat continue de modifier le
     * même document. Un brouillon venu d'ailleurs est COPIÉ dans un document
     * NEUF, parce qu'un brouillon ne peut pas changer de conversation (le
     * serveur le refuse) et que son original doit rester chez lui.
     *
     * Sans ce champ le navigateur ne peut pas trancher, et posait la copie
     * par-dessus le document courant : l'agent qui ouvrait un autre document du
     * fil écrasait celui que l'avocat avait sous les yeux.
     *
     * `null` pour un brouillon flottant, qui n'appartient à aucun fil.
     */
    conversationId: z.string().uuid().nullable(),
    /**
     * L'ACK d'intégrité du brouillon, quand il en a un.
     *
     * Transporté pour que l'ADOPTION le conserve : sans lui le document adopté
     * passerait pour un brouillon legacy sans preuve de sauvegarde, et sa sortie
     * vers le dossier exigerait un ré-export inutile.
     */
    integrityAttemptId: z.string().uuid().optional(),
    /**
     * La nature du brouillon, conservée à l'adoption.
     *
     * Sans elle le document se réenregistrait en `generated` — la valeur que le
     * chemin de remplacement pose par défaut — et une page blanche ou une pièce
     * importée changeait de nature au premier autosave qui suit.
     *
     * ⚠️ Nommée `draftKind` et non `kind` : ce dernier est le DISCRIMINANT de
     * l'union (`blank` | `draft`), deux champs homonymes ne peuvent pas coexister.
     */
    draftKind: CanvasDraftKindSchema,
  }),
]);
export type CanvasOpenDocumentSource = z.infer<typeof CanvasOpenDocumentSourceSchema>;

/** Demande backend → front de poser un autre document dans le canvas. */
export const CanvasOpenDocumentRequestPayloadSchema = z.object({
  requestId: z.string().min(1),
  source: CanvasOpenDocumentSourceSchema,
});
export type CanvasOpenDocumentRequestPayload =
  z.infer<typeof CanvasOpenDocumentRequestPayloadSchema>;

/**
 * Accusé front → back.
 *
 * `etatDeLEcran` porte les TROIS issues d'échec, parce que le remplacement chaud
 * en a trois et qu'un booléen en écraserait deux. « Rien n'a bougé » et « le
 * document précédent est perdu » n'appellent pas du tout la même phrase à
 * l'avocat : la première est anodine, la seconde exige de le prévenir. Sans ce
 * champ, l'agent ne peut pas décrire ce qu'il a sous les yeux.
 */
export const CanvasOpenDocumentResultPayloadSchema = z.discriminatedUnion('ok', [
  z.object({
    requestId: z.string().min(1),
    ok: z.literal(true),
    source: z.enum(['blank', 'draft']),
    /** Current replacement mount; optional only so old frontend ACKs parse safely. */
    mountEpoch: z.number().int().nonnegative().optional(),
  }),
  z.object({
    requestId: z.string().min(1),
    ok: z.literal(false),
    error: z.string().min(1),
    etatDeLEcran: z.enum([
      /** Le remplacement a échoué et le rollback a ramené le document précédent. */
      'document-precedent-restaure',
      /** L'échec est survenu AVANT tout remplacement : l'écran est intact. */
      'rien-na-bouge',
      /** Le remplacement a échoué et le document précédent n'a pas pu revenir. */
      'document-precedent-perdu',
      /**
       * Le brouillon demandé est à l'écran, mais son identité n'a pas pu être
       * adoptée : il a été détaché et s'enregistrera comme un NOUVEAU document.
       * L'original reste intact dans la liste, le document quitté aussi.
       */
      'document-ouvert-comme-copie',
    ]),
  }),
]);
export type CanvasOpenDocumentResultPayload =
  z.infer<typeof CanvasOpenDocumentResultPayloadSchema>;

/**
 * Combien de temps le backend attend `canvas_open_document_result` après avoir
 * émis `canvas_open_document_request` (outil `open_canvas_document`).
 *
 * Partagée parce que le navigateur doit accuser AVANT cette borne, quoi qu'il
 * arrive : passé ce délai l'outil rend « état de l'écran inconnu », et un accusé
 * tardif — même `ok:true` — n'est plus lu par personne.
 */
export const CANVAS_OPEN_DOCUMENT_ACK_TIMEOUT_MS = 30_000;

/**
 * L'échéance UNIQUE du gestionnaire navigateur de `canvas_open_document_request`.
 *
 * Tout ce que le gestionnaire attend — résidence, éditeur écrivable,
 * enregistrement du document sortant, montage du document posé, annonce —
 * reçoit ce qui RESTE de ce budget, et à l'échéance il accuse un échec qui
 * nomme l'étape en cours. Additionner des bornes par étape (14 s + 14 s + un
 * réseau non borné + 14 s + 1,5 s) dépassait la borne backend à froid.
 *
 * Quatre secondes sous `CANVAS_OPEN_DOCUMENT_ACK_TIMEOUT_MS` : la marge du
 * transit de la demande et de l'accusé sur la WebSocket. Le pire cas nominal
 * mesuré du chemin — un entrant qui ne monte pas (14 s) puis le document
 * précédent restauré — accuse à 16 s (`CANVAS_DOCUMENT_RESTORATION_TIMEOUT_MS`,
 * `WebSocketPool.ts`).
 */
export const CANVAS_OPEN_DOCUMENT_BROWSER_DEADLINE_MS = CANVAS_OPEN_DOCUMENT_ACK_TIMEOUT_MS - 4_000;

/**
 * Combien de temps le backend attend `canvas_open_result` après avoir émis
 * `canvas_open_request` (outil `open_canvas`).
 *
 * 30 s, comme les deux autres ouvertures. À 15 s, 40 ouvertures ont expiré du
 * 28/10 au 01/11 en prod, et 19 d'entre elles ont reçu leur accusé APRÈS la borne,
 * 14 dans les 11 s qui suivaient : personne ne le lisait. Un montage à froid
 * enchaîne la recherche du brouillon (8 s) et l'hydratation (14 s).
 */
export const CANVAS_OPEN_ACK_TIMEOUT_MS = 30_000;

/**
 * L'échéance UNIQUE du gestionnaire navigateur de `canvas_open_request` :
 * recherche du brouillon, hydratation et attente de l'éditeur se partagent ce
 * budget. Même marge de transit que `CANVAS_OPEN_DOCUMENT_BROWSER_DEADLINE_MS`.
 */
export const CANVAS_OPEN_BROWSER_DEADLINE_MS = CANVAS_OPEN_ACK_TIMEOUT_MS - 4_000;

/**
 * Borne d'attente du backend sur l'accusé `canvas_load_template_result`
 * (outils `load_document_into_canvas` et `compare_documents`).
 *
 * Partagée pour la même raison que `CANVAS_OPEN_DOCUMENT_ACK_TIMEOUT_MS` : un
 * accusé tardif, même `success:true`, n'est lu par personne, et l'agent dit à
 * l'avocat que sa pièce ne s'ouvre pas pendant qu'elle s'affiche (prod
 * 2026-10-19 → 23 : réponses du navigateur reçues 33 à 93 s après la demande).
 */
export const CANVAS_LOAD_TEMPLATE_ACK_TIMEOUT_MS = 30_000;

/**
 * L'échéance UNIQUE du gestionnaire navigateur de `canvas_load_template_request` :
 * enregistrement du document sortant (réseau, sans borne propre), emplacement
 * du canvas (14 s), montage froid (14 s) ou remplacement chaud (14 s, puis 10 s
 * de restauration) et annonce (1,5 s) se partagent ce budget. Même marge de
 * transit que `CANVAS_OPEN_DOCUMENT_BROWSER_DEADLINE_MS`.
 */
export const CANVAS_LOAD_TEMPLATE_BROWSER_DEADLINE_MS = CANVAS_LOAD_TEMPLATE_ACK_TIMEOUT_MS - 4_000;

// ── Génération orchestrateur : le markdown rendu, tel quel (#938) ──────────
//
// **Pourquoi une trame à part, et pourquoi du markdown brut.** Jusqu'au
// 2026-08-14, la cascade orchestrateur convertissait son markdown en un lot
// d'actions `insert_paragraph` (`markdownToCanvasActions.ts`, supprimé) : le
// backend décidait de la structure, et il en décidait MOINS BIEN que le parseur
// partagé — aucun saut de page, aucune table, le gras laissé en `**` littéral
// dans le texte de l'avocat, et un plafond de 500 paragraphes tronqué en
// silence. Or ce markdown est produit par un job worker exactement comme celui
// de la génération en flux : rien en aval du texte ne justifiait qu'ils
// divergent.
//
// La convergence se fait donc AU PARSEUR : le markdown traverse le wire tel
// quel, le navigateur le passe au `StructuralParser` de `@hector/shared-types`
// — la même classe, dans le même runtime que la génération en flux — et les
// blocs obtenus rejoignent le point d'écriture unique du canvas. Envoyer des
// blocs déjà parsés aurait exigé un second contrat de sérialisation pour
// `StructuredBlock` sans rien rapprocher des deux chemins ; envoyer un lot
// d'actions, c'était garder le convertisseur.

/**
 * Borne haute du markdown d'un acte rendu, en caractères.
 *
 * **Dimensionnée sur le cas réel, pas sur une intuition** : le plus gros rendu
 * observé tient dans 50-80k caractères, et la sonde de référence pousse 620
 * paragraphes (~40k) — soit un ordre de grandeur sous cette borne. Un acte
 * légitime ne peut pas l'atteindre.
 *
 * Ce qu'elle exclut est l'accident : le producteur est notre propre worker,
 * mais rien n'écrivait jusqu'ici que sa sortie est bornée, et l'écriture est
 * une **insertion HTML unique** côté navigateur — un payload pathologique n'y
 * serait pas lent, il figerait l'onglet de l'avocat. La borne rend l'invariant
 * lisible au lieu de le laisser déduire du producteur.
 */
const CANVAS_GENERATION_MARKDOWN_MAX_CHARS = 2_000_000;

/** Demande backend → front d'écrire un acte rendu par un job worker. */
export const CanvasGenerationRequestPayloadSchema = z.object({
  requestId: z.string().min(1),
  /** Le markdown du worker de rendu, VERBATIM — aucune pré-transformation. */
  markdown: z
    .string()
    .min(1)
    .max(
      CANVAS_GENERATION_MARKDOWN_MAX_CHARS,
      `markdown dépasse ${CANVAS_GENERATION_MARKDOWN_MAX_CHARS} caractères : un acte rendu légitime tient dans `
        + '50-80k. Un tel volume signale un rendu accidentel, pas un acte long — le canvas l\'écrirait en une '
        + 'insertion HTML unique et figerait l\'onglet de l\'avocat.',
    ),
  /** Le job qui a produit ce markdown — porté pour les messages d'erreur. */
  jobId: z.string().min(1).optional(),
  /**
   * Le papier à en-tête du document que ce rendu DÉMARRE. Présent, le navigateur pose une page
   * neuve sur ce modèle et y écrit le rendu, au lieu de l'écrire à la suite du document ouvert
   * (Word importé, modèle de bibliothèque, autre brouillon du fil), qui reste dans le fil.
   */
  depart: CanvasDocumentDepartSchema.optional(),
});
export type CanvasGenerationRequestPayload = z.infer<typeof CanvasGenerationRequestPayloadSchema>;

/**
 * Accusé front → back. `blocsEcrits` est le nombre de blocs structurés
 * réellement posés dans le document : c'est la seule mesure qui distingue « le
 * canvas a écrit l'acte » de « le parseur n'a rien tiré de ce markdown », un
 * mode d'échec réel (le parseur jette tout ce qui précède le premier `#` ou
 * `[Hector-`, donc un rendu sans titre ni tag de style produit zéro bloc).
 */
export const CanvasGenerationResultPayloadSchema = z.discriminatedUnion('ok', [
  z.object({
    requestId: z.string().min(1),
    ok: z.literal(true),
    blocsEcrits: z.number().int().nonnegative(),
    /** Whether the backend has received the post-insertion Canvas revision. */
    canvasAgentStateSynchronized: z.boolean().optional(),
  }),
  z.object({
    requestId: z.string().min(1),
    ok: z.literal(false),
    error: z.string().min(1),
  }),
]);

// ── Shapes de lecture (résultats canvas_state_result) ──────────────────────
// Interfaces TS pures (pas de Zod : produites par le front, consommées par le
// LLM en JSON — la validation runtime n'apporterait rien côté producteur).

/** Un paragraphe du canvas tel que relu par l'agent (`get_canvas_paragraphs`). */
export interface CanvasParagraphSummary {
  index: number;
  styleKey: string;
  text: string;
  hasBold?: boolean;
  hasItalic?: boolean;
  hasUnderline?: boolean;
  hasHighlight?: boolean;
  /** Présent SSI au moins un run du paragraphe porte une ancre de commentaire. */
  comments?: Array<{
    commentId: string;
    text: string;
    commentText: string;
    isInternal?: boolean;
    status?: string;
  }>;
  align?: string;
}

/** Sélection courante du canvas (`get_canvas_selection`). */
export interface CanvasSelectionSummary {
  has_selection: boolean;
  paragraph_index?: number;
  end_paragraph_index?: number;
  text?: string;
}

/** Résultat unitaire d'une action (renvoyé dans `canvas_actions_result.results`). */
export interface CanvasActionResult {
  action: CanvasAction;
  success: boolean;
  error?: string;
  /**
   * La conduite à tenir — voir {@link RecoveryInstructionSchema}.
   *
   * ⚠️ **Porté PAR ACTION, et non sur l'enveloppe, parce que l'enveloppe
   * n'arrive pas jusqu'à l'agent.** Deux étranglements successifs l'imposent,
   * tous deux côté backend : `CanvasActionsResultSchema`
   * (`websocket/schemas.ts`) ne déclare que `{ requestId, results }`, et Zod
   * retirerait tout champ posé à côté ; puis `ConnectionManager` résout
   * l'attente sur `payload.results` SEUL — l'enveloppe est défaite et jetée.
   * `results[]` y est typé `z.array(z.unknown())`, donc ce qui voyage DANS une
   * entrée passe intact.
   *
   * Conséquence assumée : sur un refus qui vaut pour tout le lot, la même
   * consigne est répétée sur chaque entrée. C'est déjà la doctrine du champ
   * `error` voisin (« le backend relaie `results[]` tel quel au LLM, qui ne
   * lira peut-être qu'une ligne ») — une consigne d'arrêt posée sur la seule
   * première entrée serait manquée exactement dans ce cas-là.
   */
  recovery?: RecoveryInstruction;
}

export * from './mountIdentity.js';
export * from './actionCatalog.js';
export * from './imageBytes.js';
export * from './canvasTarget.js';
export * from './canvasFormat.js';
export * from './canvasReplace.js';
export * from './superdocCapabilityContract.js';
export * from './canvasDraft.js';
export * from './docxIntegrity.js';
export * from './canvasAgentState.js';
export * from './textListPrefix.js';
